import React, { useMemo, useState } from "react"

import CatchModal from "~/library/CatchModal"

import { apiPayments } from "~/routes"
import { useToast } from "~/utils/Toast"
import getAppConfig from "~/utils/getAppConfig"
import { formatCurrency } from "~/utils/helpers"
import useSavedPaymentMethods from "~/hooks/useSavedPaymentMethods"

import PaymentItemsTable from "~/components/payments/PaymentItemsTable"
import { confirmRequiredAction, formatSavedCard, payableLabels } from "~/utils/paymentModalHelpers"

import type PaymentItem from "~/types/PaymentItem"
import type SavedPaymentMethod from "~/types/SavedPaymentMethod"
import type { PayablePaymentResource } from "~/types/PayablePaymentResource"
import type { PaymentKind } from "~/components/payments/PayablePaymentModal"
import usePaymentItemsState from "~/hooks/usePaymentItemsState"
import { calculateProcessingFeeCents } from "~/utils/processingFee"

interface Props {
  onClose: () => void
  onSuccess: () => void | Promise<void>
  resource: PayablePaymentResource
  initialItems?: PaymentItem[]
  savedPaymentMethod?: SavedPaymentMethod | null
  showTypeSelector: boolean
  paymentType: PaymentKind
  onPaymentTypeChange: (value: PaymentKind) => void
  totalAmountCents?: number | null
  paidAmountCents?: number | null
  remainingAmountCents?: number | null
}

const SavedCardChargeModal: React.FC<Props> = ({
  onClose,
  onSuccess,
  resource,
  initialItems = [],
  savedPaymentMethod = null,
  showTypeSelector,
  paymentType,
  onPaymentTypeChange,
  totalAmountCents,
  paidAmountCents = null,
  remainingAmountCents = null,
}) => {
  const toast = useToast()
  const { venue, venueSlug, processingFeeRates } = getAppConfig()
  const currencyIso = resource.currency || "USD"
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { savedPaymentMethods } = useSavedPaymentMethods(resource.payableType, resource.payableId)

  const { paymentItems, setPaymentItems, visibleItems, hasValidItems } = usePaymentItemsState({
    initialItems,
    currencyIso,
  })
  const processingFeeCents = useMemo(() => {
    const subtotalAmountCents = visibleItems.reduce(
      (total, item) => total + item.quantity * item.amountCents,
      0
    )

    return calculateProcessingFeeCents({
      catchMarkupFixedCents: venue.catchMarkupFixedCents,
      catchMarkupPercentage: venue.catchMarkupPercentage,
      currency: currencyIso,
      processingFeeRates,
      subtotalAmountCents,
    })
  }, [currencyIso, processingFeeRates, venue, visibleItems])

  const activeSavedCard = useMemo(() => {
    if (savedPaymentMethod) return savedPaymentMethod
    return savedPaymentMethods.find((method) => method.isDefault) || savedPaymentMethods[0] || null
  }, [savedPaymentMethod, savedPaymentMethods])

  const handleSubmit = () => {
    if (visibleItems.length === 0) {
      toast("At least one payment item is required", "danger")
      return
    }

    if (!hasValidItems) {
      toast("Please add at least one valid item with description and amount", "danger")
      return
    }

    if (!activeSavedCard) {
      toast("Default saved card is required", "danger")
      return
    }

    const nextChargeAttemptToken = crypto.randomUUID()
    setIsSubmitting(true)

    apiPayments
      .chargeSavedCard({
        data: {
          venueSlug,
          payableId: resource.payableId,
          payableType: resource.payableType,
          savedPaymentMethodId: activeSavedCard.id,
          chargeAttemptToken: nextChargeAttemptToken,
          paymentItemsAttributes: visibleItems.map((item) => ({
            description: item.description,
            quantity: item.quantity,
            amountCents: item.amountCents,
          })),
        },
      })
      .then(async () => {
        toast("Card charged successfully", "success")
        await onSuccess()
        onClose()
      })
      .catch(async (error) => {
        if (error.body?.status === "requires_action" && error.body?.clientSecret) {
          try {
            await confirmRequiredAction(
              error.body.clientSecret,
              error.body.stripeConnectedAccountId
            )
            toast("Card authenticated and charged successfully", "success")
            await onSuccess()
            onClose()
          } catch (confirmError: unknown) {
            const message = confirmError instanceof Error ? confirmError.message : null

            toast(message || "Authentication is required to complete this payment.", "danger")
          }

          return
        }

        toast(error.body?.errors?.join(", ") || "Failed to charge saved card", "danger")
      })
      .finally(() => setIsSubmitting(false))
  }

  return (
    <CatchModal
      isOpen={true}
      onClose={onClose}
      title={`Charge Saved Card — ${payableLabels[resource.payableType]}`}
      size="lg"
    >
      <div className="space-y-4">
        {showTypeSelector && (
          <div className="row mb-3">
            <div className="col-12 d-flex gap-2 align-items-center flex-wrap">
              <label className="form-label mb-0 text-muted fw-bold">Type</label>

              <select
                className="form-select w-auto"
                value={paymentType}
                onChange={(e) => onPaymentTypeChange(e.target.value as PaymentKind)}
                disabled={isSubmitting}
              >
                <option value="Invoice">Invoice (Stripe)</option>
                <option value="Manual">Manual (record paid)</option>
                <option value="SavedCard">Saved Card Charge</option>
              </select>
            </div>
          </div>
        )}

        <div className="row mb-4">
          <div className="col-6">
            <h6 className="text-muted mb-2">CHARGE FOR</h6>

            <div>
              <strong>{resource.summary.title}</strong>
              <br />

              {resource.summary.subtitle && (
                <>
                  <span className="text-muted">{resource.summary.subtitle}</span>
                  <br />
                </>
              )}

              {resource.summary.meta && (
                <>
                  <span className="text-muted">{resource.summary.meta}</span>
                  <br />
                </>
              )}

              <span className="badge bg-secondary text-uppercase">
                {payableLabels[resource.payableType]}
              </span>
            </div>
          </div>

          <div className="col-6">
            {!resource.customer ? (
              <div className="alert alert-warning mb-0">No client information available.</div>
            ) : (
              <div className="row">
                <div className="col-12 mb-3">
                  <h6 className="text-muted mb-2">BILL TO</h6>
                  <div>
                    <strong>{resource.customer.name}</strong>
                    <br />

                    {resource.customer.email && (
                      <>
                        <span className="text-muted">{resource.customer.email}</span>
                        <br />
                      </>
                    )}

                    {resource.customer.phone && (
                      <span className="text-muted">{resource.customer.phone}</span>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {activeSavedCard && (
            <div className="col-12 mt-2">
              <h6 className="text-muted mb-2">SAVED CARD</h6>

              <div className="d-flex align-items-center gap-2 flex-wrap">
                <strong>{formatSavedCard(activeSavedCard)}</strong>

                {activeSavedCard.isDefault && <span className="badge bg-success">Default</span>}

                {activeSavedCard.linkedToCurrentPayable && (
                  <span className="badge bg-primary">Used</span>
                )}
              </div>
            </div>
          )}

          {!activeSavedCard && (
            <div className="col-12 mt-2">
              <div className="alert alert-warning mb-0">
                No default saved card is available for this record.
              </div>
            </div>
          )}
          {totalAmountCents != null &&
            paidAmountCents !== null &&
            remainingAmountCents !== null && (
              <div className="col-12 mt-2">
                <div className="row g-3">
                  <div className="col-md-4">
                    <div className="border rounded p-3 h-100">
                      <div className="text-muted small mb-1">Total</div>
                      <div className="fw-semibold">
                        {formatCurrency({
                          cents: totalAmountCents,
                          currency_iso: currencyIso,
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="border rounded p-3 h-100">
                      <div className="text-muted small mb-1">Paid</div>
                      <div className="fw-semibold">
                        {formatCurrency({
                          cents: paidAmountCents,
                          currency_iso: currencyIso,
                        })}
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <div className="border rounded p-3 h-100">
                      <div className="text-muted small mb-1">Balance Remaining</div>
                      <div className="fw-semibold">
                        {formatCurrency({
                          cents: remainingAmountCents,
                          currency_iso: currencyIso,
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

          {resource.summary.details && (
            <div className="col-12 mt-2">
              <h6 className="text-muted mb-2">ADDITIONAL DETAILS</h6>
              <p className="text-muted mb-0">{resource.summary.details}</p>
            </div>
          )}
        </div>

        <PaymentItemsTable
          title="ITEMS TO CHARGE"
          items={paymentItems}
          setItems={setPaymentItems}
          currency={resource.currency || "USD"}
          isSubmitting={isSubmitting}
          isSavedCardFlow={true}
          activeSavedCard={activeSavedCard}
          processingFeeCents={processingFeeCents}
          processingFeePayer={venue.processingFeePayer}
          placeholderTitle={`Charge for ${resource.summary.title}`}
        />

        <div className="d-flex gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-secondary ms-auto"
            disabled={isSubmitting}
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={handleSubmit}
            className="btn btn-primary"
            disabled={isSubmitting || !resource.customer || !hasValidItems || !activeSavedCard}
          >
            {isSubmitting ? "Charging..." : "Charge Card"}
          </button>
        </div>
      </div>
    </CatchModal>
  )
}

export default SavedCardChargeModal
